import { Component, effect, ElementRef, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { notEmptyOrSpaces } from '@sbd-doug-frontend/shared/lib/public-validators';
import { SharedChangePasswordService } from '../change-password.service';


export interface DialogChangePasswordObject {
  password: string;
  confirm_password: string;
  verificationCode?: number;
}

@Component({
  selector: 'shared-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styles: [
    `
      .fuse-confirmation-dialog-panel {
        @screen md {
          @apply w-128;
        }

        .mat-mdc-dialog-container {
          .mat-mdc-dialog-surface {
            padding: 0 !important;
          }
        }
      }

      @media screen and (min-width: 768px) {
        .md\\:w-600px {
          width: 600px !important;
        }
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})


export class ChangePasswordDialogComponent implements OnInit {
  @ViewChild('verificationCodeInput') verificationCodeInput: ElementRef | undefined;
  @ViewChild('newPasswordInput') newPasswordInput: ElementRef | undefined;

  disableChangeButton = false;
  validatePasswordMatch = (control: AbstractControl): { [key: string]: any } | null => {
    const password = this.pwForm?.get('new_password')?.value as string;
    const passwordConfirm = control.value as string;

    if (password !== passwordConfirm) {
      return { passwordMatch: true };
    }

    return null;
  };
  requestMfaCode = false;
  pwForm: UntypedFormGroup;
  loading = false;
  passwordChangeInProgress = this._changePasswordService.passwordChangeInProgress;
  mfaCodeSent = this._changePasswordService.mfaCodeSent;
  showMessage = false;


  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _cathy: CathyService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private _changePasswordService: SharedChangePasswordService,
  ) {
    effect(() => {
      if (this.requestMfaCode) {
        this.disableChangeButton = true;
        if ( this._changePasswordService.mfaCodeSent()) {
          this.showMessage = true;
          this.pwForm.enable();
          this.disableChangeButton = false;
          this.pwForm?.get('confirm_password')?.disable()
          this.pwForm?.get('new_password')?.disable()
        } else {
          this.disableChangeButton = true;
          this.showMessage = false;
          this.pwForm.disable();
        }

        this.fieldFocusHandler();
      }
    });

    if (this.data?.requestMfaCode) {
      this.requestMfaCode = this.data.requestMfaCode;
      this.pwForm = this._formBuilder.group({
        new_password: new FormControl('', [notEmptyOrSpaces, this.validPassword, Validators.required]),
        confirm_password: new FormControl('', [notEmptyOrSpaces, Validators.required, this.validatePasswordMatch]),
        mfaCode: new FormControl('', Validators.required),
      });

      if (this.data?.loggedInUser) {
        this._changePasswordService.mfaCodeRequested.emit({
            email: this.data?.loggedInUser.email,
          },
        );
      }
    } else {
      this.pwForm = this._formBuilder.group({
        new_password: new FormControl('', [notEmptyOrSpaces, this.validPassword, Validators.required]),
        confirm_password: new FormControl('', [notEmptyOrSpaces, Validators.required, this.validatePasswordMatch]),
      });
    }
  }

  fieldFocusHandler() {
    if (this.pwForm.enabled) {
      if (this.pwForm.contains('mfaCode')) {
        this.verificationCodeInput?.nativeElement.focus();
      } else {
        this.newPasswordInput?.nativeElement.focus();
      }
    }
  }

  ngOnInit(): void {

  }

  isPasswordValid() {
    const passwordMatcher = this.pwForm.get('new_password')?.value ?? '';
    const matcher = new RegExp(
      '(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*()-_=+{};:,<.>])(?=.*[0-9]).*',
    );
    const isValid = matcher.test(passwordMatcher);
    if (!isValid) {
      this.pwForm.get('new_password')?.setErrors({ invalidpassword: true });
    } else {
      this.pwForm.get('new_password')?.setErrors(null);
      return;
    }
  }

  validPassword(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    const matcher = new RegExp(
      '(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*()-_=+{};:,<.>])(?=.*[0-9]).*',
    );
    const isValid = matcher.test(control.value);

    return isValid
      ? null
      : { pwError: 'Password must follow the password rules' };
  }

  isConfirmedPassword() {
    if (
      this.pwForm.get('confirm_password')?.value !==
      this.pwForm.get('new_password')?.value
    ) {
      // this.pwForm.controls.confirm_password.invalid;
      this.pwForm.get('confirm_password')?.setErrors({ 'notamatch': true });
    } else {
      return;
    }
  }

  onCancel(): void {
    this._changePasswordService.mfaCodeSent.set(false);
    this.dialogRef.close();
  }

  onSubmitForChange(event: any) {
    if (event !== null) {
      event.preventDefault();
    }

    if (this.pwForm.invalid) {
      this._cathy.error('Please fill in the required fields.');
      return;
    }

    this.passwordChangeInProgress.set(true);
    this._changePasswordService.mfaCodeSent.set(false);
    const pwConversionObject = {
      password: this.pwForm.get('new_password')?.value,
      confirm_password: this.pwForm.get('confirm_password')?.value,
      ...(this.requestMfaCode && { verificationCode: this.pwForm.get('mfaCode')?.value }),
    };

    this._changePasswordService.dialogPasswordSubmitted.emit({
        passwordObject: pwConversionObject,
        dialogRef: this.dialogRef,
      },
    );
  }

  checkVerificationCode(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    if (input.value.length === 6) {
      this.pwForm?.get('confirm_password')?.enable()
      this.pwForm?.get('new_password')?.enable()
    } else {
      this.pwForm?.get('confirm_password')?.disable()
      this.pwForm?.get('new_password')?.disable()
    }
    this.pwForm?.get('mfaCode')?.setValue(input.value);
  }

  get mfaCode() {
    return this.pwForm.get('mfaCode');
  }
}
